/**
 * Builds `executeActions` steps for claiming V1 manager fees + V2 UGM grid fees.
 * Shared by the global header and the lobby Rewards sheet so both hit the same contracts.
 */

import { encodeFunctionData, type Hex } from 'viem';
import {
  getContractAddresses,
  TAKEOVER_MANAGER_ABI,
  TAKEOVER_LEGACY_FACTORY_ABI,
  UGM_ABI,
} from '@/lib/contracts';
import { parseGridId } from '@/lib/utils';
import type { CreatorTaxBreakdown, V2GridBreakdown } from '@/lib/cache/rpc-cache';
import type { PendingFeesCachedShape } from '@/lib/fetchers/pending-fees-query';

export type FeeClaimStep = {
  id: string;
  label: string;
  description: string;
  call: { to: Hex; data: Hex };
};

type PortfolioSeatLike = {
  pool?: {
    managerAddress?: string;
    graduationManagerAddress?: string;
  };
};

/**
 * Discover manager addresses from portfolio seats + pending-fees server list,
 * mirroring `useUserStats` / the main app header.
 */
export function collectFeeManagerAddresses(
  seats: PortfolioSeatLike[],
  serverManagers: string[],
): { knownManagers: Hex[]; gradManagerAddresses: Hex[] } {
  const gradManagerSet = new Set<string>();
  for (const seat of seats) {
    const g = seat.pool?.graduationManagerAddress;
    if (g) gradManagerSet.add(g.toLowerCase());
  }
  const managerSet = new Set<string>();
  for (const seat of seats) {
    const addr = seat.pool?.managerAddress;
    if (addr && !gradManagerSet.has(addr.toLowerCase())) {
      managerSet.add(addr);
    }
  }
  for (const addr of serverManagers) {
    managerSet.add(addr);
  }
  return {
    knownManagers: [...managerSet] as Hex[],
    gradManagerAddresses: [...gradManagerSet] as Hex[],
  };
}

/**
 * Async: reads `getHolderPools` on each manager (plus legacy factory) to build V1 claim steps,
 * then appends V2 `UGM.claimFees` calls grouped by manager — same sequencing as `header.tsx`.
 */
export async function buildSeatFeeClaimSteps(
  walletAddress: Hex,
  pending: PendingFeesCachedShape,
  portfolioSeats: PortfolioSeatLike[],
): Promise<FeeClaimStep[]> {
  const { createPublicClient, http } = await import('viem');
  const { base, baseSepolia } = await import('viem/chains');
  const chainId = process.env.NEXT_PUBLIC_CHAIN_ID;
  const chain = chainId === '8453' ? base : baseSepolia;
  const rpcUrl = process.env.NEXT_PUBLIC_RPC_URL || chain.rpcUrls.default.http[0];
  const addresses = getContractAddresses();
  const publicClient = createPublicClient({ chain, transport: http(rpcUrl) });

  const { knownManagers, gradManagerAddresses } = collectFeeManagerAddresses(
    portfolioSeats,
    pending.serverManagers,
  );
  const gradSet = new Set(gradManagerAddresses.map((a) => a.toLowerCase()));

  type ManagerClaim = { managerAddress: Hex; holderPools: Hex[] };
  const claimableManagers: ManagerClaim[] = [];

  for (const mgr of knownManagers) {
    try {
      const pools = (await publicClient.readContract({
        address: mgr,
        abi: TAKEOVER_MANAGER_ABI,
        functionName: 'getHolderPools',
        args: [walletAddress],
      })) as Hex[];
      if (pools.length > 0) {
        claimableManagers.push({ managerAddress: mgr, holderPools: pools });
      }
    } catch {
      /* skip */
    }
  }

  if (addresses.TAKEOVER_LEGACY_FACTORY && addresses.TAKEOVER_LEGACY_FACTORY !== ('0x' as Hex)) {
    try {
      const legacyAddrs = (await publicClient.readContract({
        address: addresses.TAKEOVER_LEGACY_FACTORY,
        abi: TAKEOVER_LEGACY_FACTORY_ABI,
        functionName: 'allManagerAddresses',
      })) as Hex[];

      const knownSet = new Set(knownManagers.map((a) => a.toLowerCase()));
      const unknownLegacy = legacyAddrs.filter(
        (a) => !knownSet.has(a.toLowerCase()) && !gradSet.has(a.toLowerCase()),
      );

      const legacyResults = await Promise.all(
        unknownLegacy.map(async (mgr) => {
          try {
            const pools = (await publicClient.readContract({
              address: mgr,
              abi: TAKEOVER_MANAGER_ABI,
              functionName: 'getHolderPools',
              args: [walletAddress],
            })) as Hex[];
            return pools.length > 0 ? { managerAddress: mgr, holderPools: pools } : null;
          } catch {
            return null;
          }
        }),
      );
      for (const r of legacyResults) {
        if (r) claimableManagers.push(r);
      }
    } catch {
      /* factory not deployed */
    }
  }

  const steps: FeeClaimStep[] = [];
  const v2Breakdown = pending.v2Breakdown;
  const hasV2 = v2Breakdown.some(
    (b) => BigInt(b.rawPendingFees || b.pendingFees || '0') > 0n,
  );

  claimableManagers.forEach((mgr, idx) => {
    const claimData = encodeFunctionData({
      abi: TAKEOVER_MANAGER_ABI,
      functionName: 'claimFees',
      args: [mgr.holderPools],
    });
    steps.push({
      id: `claim-fees-${idx}`,
      label: hasV2
        ? `Claim V1 Fees (Manager ${idx + 1})`
        : claimableManagers.length === 1
          ? 'Claim ETH Fees'
          : `Claim Fees (Manager ${idx + 1})`,
      description: `Claiming from ${mgr.holderPools.length} pool${mgr.holderPools.length !== 1 ? 's' : ''}`,
      call: { to: mgr.managerAddress, data: claimData },
    });
  });

  if (v2Breakdown.length > 0) {
    const byManager = new Map<string, string[]>();
    for (const b of v2Breakdown) {
      if (BigInt(b.rawPendingFees || b.pendingFees || '0') <= 0n) continue;
      // Skip rows whose `managerAddress` is unresolved instead of
      // silently retargeting the V20 UGM (`addresses.UGM`). The
      // previous fallback meant a V22 grid whose indexer row briefly
      // dropped its `managerAddress` produced a `claimFees(uint256[])`
      // call against the V20 contract — which encodes the same
      // selector but stores no state for that grid id, reverting on
      // chain after a confirmed wallet sign. Better to skip the step
      // and surface the partial breakdown than ship a wrong-contract
      // tx the user paid gas to discover.
      if (!b.managerAddress) {
        console.warn('[fee-claim] dropping v2Breakdown row with no managerAddress', { gridId: b.gridId });
        continue;
      }
      const mgr = b.managerAddress;
      if (!byManager.has(mgr)) byManager.set(mgr, []);
      byManager.get(mgr)!.push(b.gridId);
    }
    let idx = 0;
    for (const [mgr, gridIds] of byManager) {
      const data = encodeFunctionData({
        abi: UGM_ABI,
        functionName: 'claimFees',
        args: [gridIds.map((id) => BigInt(parseGridId(id).numericId))],
      });
      steps.push({
        id: `claim-fees-v2${idx > 0 ? `-${idx}` : ''}`,
        label: 'Claim Grid Fees',
        description: `Collecting rewards & claiming from ${gridIds.length} grid${gridIds.length !== 1 ? 's' : ''}`,
        call: { to: mgr as Hex, data },
      });
      idx++;
    }
  }

  for (const tax of pending.creatorTaxBreakdown ?? []) {
    let raw = 0n;
    try {
      raw = BigInt(tax.taxRevenue || '0');
    } catch {
      continue;
    }
    if (raw <= 0n) continue;
    if (!tax.managerAddress) {
      console.warn('[fee-claim] dropping creatorTax row with no managerAddress', { gridId: tax.gridId });
      continue;
    }
    const taxData = encodeFunctionData({
      abi: UGM_ABI,
      functionName: 'withdrawTaxRevenue',
      args: [BigInt(parseGridId(tax.gridId).numericId)],
    });
    const target = tax.managerAddress as Hex;
    steps.push({
      id: `withdraw-tax-${tax.gridId}`,
      label: tax.gridName ? `Withdraw tax · ${tax.gridName}` : `Withdraw tax · Grid #${parseGridId(tax.gridId).numericId}`,
      description: 'Collecting board tax revenue',
      call: { to: target, data: taxData },
    });
  }

  return steps;
}

export function filterV2BreakdownForGrids(
  breakdown: V2GridBreakdown[],
  gridIds: string[],
): V2GridBreakdown[] {
  const set = new Set(gridIds);
  return breakdown.filter((b) => set.has(b.gridId));
}

export function buildSingleGridTaxWithdrawStep(
  gridId: string,
  creatorTaxBreakdown: CreatorTaxBreakdown[],
): FeeClaimStep | null {
  const tax = creatorTaxBreakdown.find((t) => t.gridId === gridId);
  if (!tax) return null;
  let raw = 0n;
  try {
    raw = BigInt(tax.taxRevenue || '0');
  } catch {
    return null;
  }
  if (raw <= 0n) return null;
  if (!tax.managerAddress) {
    console.warn('[fee-claim] cannot build single-grid tax withdraw step without managerAddress', { gridId });
    return null;
  }
  const target = tax.managerAddress as Hex;
  const data = encodeFunctionData({
    abi: UGM_ABI,
    functionName: 'withdrawTaxRevenue',
    args: [BigInt(parseGridId(gridId).numericId)],
  });
  const label = tax.gridName
    ? `Withdraw tax · ${tax.gridName}`
    : `Withdraw tax · Grid #${parseGridId(gridId).numericId}`;
  return {
    id: `withdraw-tax-${gridId}`,
    label,
    description: 'Collecting board tax revenue',
    call: { to: target, data },
  };
}

export async function buildSingleGridFeeClaimStep(
  gridId: string,
  breakdown: V2GridBreakdown[],
): Promise<FeeClaimStep | null> {
  const grid = breakdown.find((b) => b.gridId === gridId);
  if (!grid || BigInt(grid.rawPendingFees || grid.pendingFees || '0') <= 0n) return null;
  if (!grid.managerAddress) {
    console.warn('[fee-claim] cannot build single-grid claim step without managerAddress', { gridId });
    return null;
  }
  const targetUgm = grid.managerAddress as Hex;
  const data = encodeFunctionData({
    abi: UGM_ABI,
    functionName: 'claimFees',
    args: [[BigInt(parseGridId(gridId).numericId)]],
  });
  const label = grid.gridName
    ? `Claim from ${grid.gridName}`
    : `Claim Grid #${parseGridId(gridId).numericId}`;
  return {
    id: `claim-grid-${gridId}`,
    label,
    description: 'Collecting rewards & claiming fees',
    call: { to: targetUgm, data },
  };
}
